import {
  Grid,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useContext, useEffect, useRef } from "react";
import { CRUDContext, RoleModel } from "./RolesTable";
import { endpoints } from "../../Utils/UrlEnum";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { FormsIds, MainSliderData } from "../../Utils/Constants";
import Config from "../../Utils/Config";
import { onFormError } from "../../Utils/Utils";
import * as yup from "yup";

interface RoleFormProps {
  setContextValue: any;
  onClose: () => void;
  selectedRole?: any;
}

const RoleFormValidationSchema = yup.object().shape({
  name: yup.string().required(Vocabulary.requiredField),
  priority: yup
    .number()
    .required(Vocabulary.requiredField)
    .min(0, "Prioritatea trebuie să fie cel puțin 0"),
  allowedRoutes: yup.array().of(yup.string()).default([]),
});

export default function RoleForm(props: RoleFormProps) {
  const roleContext: RoleModel = useContext(
    props.selectedRole ? props.selectedRole : CRUDContext
  );
  const ref: any = useRef();
  const firstUpdate = useRef(true);

  const { register, control, handleSubmit, getValues, formState, reset } =
    useForm({
      resolver: yupResolver(RoleFormValidationSchema),
      mode: "onChange",
      reValidateMode: "onChange",
      defaultValues: {
        name: "",
        priority: 0,
        allowedRoutes: [] as string[],
      },
    });
  const formData = useWatch({ control: control });

  useEffect(() => {
    if (formData && !firstUpdate.current) {
      props.setContextValue({
        ...formData,
      });
    }
  }, [formData]);

  useEffect(() => {
    if (roleContext.name && roleContext.name !== "") {
      reset(roleContext);
    }
  }, []);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (!firstUpdate.current) ref.current.submit();
  }, []);

  const updateElement = async (model: any) => {
    const url = `${endpoints.rolesEndpoint}/${roleContext?._id}`;
    const res = (await updateData(url, model)) as any;
    if (!res || res?.error) {
      return;
    }
    props.setContextValue({
      name: "",
      priority: 0,
      allowedRoutes: [],
      isSystem: false,
    });
    props.onClose();
  };

  const createElement = async (model: any) => {
    const url = `${endpoints.rolesEndpoint}`;
    const res = (await postData(url, model)) as any;
    if (!res || res?.error) {
      return;
    }
    props.onClose();
    props.setContextValue({
      name: "",
      priority: 0,
      allowedRoutes: [],
      isSystem: false,
    });
  };

  const onSubmit = (model: any) => {
    roleContext._id ? updateElement(model) : createElement(model);
  };

  // Get all available routes from MainSliderData
  const getAllRoutes = () => {
    const routes: Array<{ path: string; name: string; parentName?: string }> = [];
    const seenPaths = new Set<string>();

    MainSliderData.forEach((item) => {
      // Add parent routes that have direct paths (not just containers)
      if (item.path && item.path !== "" && !seenPaths.has(item.path)) {
        routes.push({ path: item.path, name: item.name });
        seenPaths.add(item.path);
      }

      // For items with children, extract parent routes from child paths
      if (item.children && item.children.length > 0) {
        // Group children by their parent route prefix
        const childrenByPrefix = new Map<string, any[]>();

        item.children.forEach((child) => {
          if (child.path && child.path !== "") {
            // Extract parent route prefix from child path
            // Example: /whatsApp/phoneNumbers -> /whatsApp
            const pathParts = child.path.split('/').filter(Boolean);
            const parentRoute = pathParts.length > 1 ? `/${pathParts[0]}` : child.path;

            if (!childrenByPrefix.has(parentRoute)) {
              childrenByPrefix.set(parentRoute, []);
            }
            childrenByPrefix.get(parentRoute)!.push(child);
          }
        });

        // Add one route per parent prefix (not per child)
        childrenByPrefix.forEach((children, parentRoute) => {
          if (!seenPaths.has(parentRoute)) {
            routes.push({
              path: parentRoute,
              name: `${item.name} (${children.map((c: any) => c.name).join(', ')})`,
              parentName: item.name
            });
            seenPaths.add(parentRoute);
          }
        });
      }
    });

    return routes;
  };

  const availableRoutes = getAllRoutes();

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      ref={ref}
      id={FormsIds.roleForm}
    >
      <Grid container spacing={Config.standardGridSpacing}>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("name")}
            id="name"
            name="name"
            label={Vocabulary.roleName}
            value={getValues("name") || ""}
            fullWidth
            variant="outlined"
            error={typeof formState.errors.name === "object"}
            helperText={formState.errors.name?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("priority")}
            value={getValues("priority") || 0}
            name="priority"
            id="priority"
            type="number"
            label={Vocabulary.rolePriority}
            fullWidth
            variant="outlined"
            error={typeof formState.errors.priority === "object"}
            helperText={formState.errors.priority?.message?.toString()}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl component="fieldset" variant="outlined">
            <FormLabel component="legend">
              {Vocabulary.selectAccessiblePages}
            </FormLabel>
            <FormGroup>
              <Grid container spacing={2}>
                {availableRoutes.map((route) => (
                  <Grid item xs={12} sm={6} md={4} key={route.path}>
                    <Controller
                      name="allowedRoutes"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                (getValues("allowedRoutes") || []).includes(
                                  route.path
                                )
                              }
                              onChange={(e) => {
                                const currentRoutes =
                                  getValues("allowedRoutes") || [];
                                if (e.target.checked) {
                                  field.onChange([...currentRoutes, route.path]);
                                } else {
                                  field.onChange(
                                    currentRoutes.filter(
                                      (r: string) => r !== route.path
                                    )
                                  );
                                }
                              }}
                            />
                          }
                          label={route.name}
                        />
                      )}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
    </form>
  );
}
